import {Checkbox, FormControlLabel, Grid, Typography} from '@mui/material';
import {
  ThankYouView,
} from './thank-you-view';
import {Button} from '@finpay-development/shared-components';
import {
  ApplicationStatusState,
  GetError,
} from '../../../security/model/application-status';
import MobilePaymentsLoader
  from '../../../shared/components/mobile-payments-loader';
import React, {useState} from 'react';
import {
  ApprovalState,
  ClientDetails,
} from './payment-program-enrollment/enrollment-approval-view';

interface Props {
  pageContent: {
    title: string
    body: {
      intro: string
      details: React.ReactElement
      programAgreementDetails?: React.ReactElement
      explanation: string
      instructions: string
      checkboxLabel: string
    }
    footerNotes: string[]
  };
  docModal: React.ReactElement;
  displayThankYouView: boolean;
  clientDetails: ClientDetails;
  modalOpenState: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>,
  ];
  setMessage: (msg: ApplicationStatusState) => void;
  handleApprovedBtnOnClick: () => Promise<void>;
  thankYouViewConfig: {
    thankYouText: string
    enableSurvey?: boolean
  };
}

const TEXT_BLOCK_MARGIN_BOTTOM = '2rem';

export const ApprovalView = ({
                               pageContent: {
                                 title,
                                 body: {
                                   intro,
                                   details,
                                   programAgreementDetails,
                                   explanation,
                                   instructions,
                                   checkboxLabel,
                                 },
                                 footerNotes,
                               },
                               docModal,
                               displayThankYouView,
                               clientDetails,
                               modalOpenState,
                               setMessage,
                               handleApprovedBtnOnClick,
                               thankYouViewConfig: {
                                 thankYouText,
                                 enableSurvey,
                               },
                             }: Props) => {

  const [modalOpen, setModalOpen] = modalOpenState;
  const [isFormProcessing, setIsFormProcessing] = useState<boolean>(false);
  const [approvalReq, setApprovalReq] = useState<ApprovalState>({
    isCheckboxChecked: false,
    isAuthDocViewed: false,
  });

  return (

      <Grid
          item
          sx={{
            padding: '1rem 2rem',
            maxWidth: '535px',
            minWidth: '300px',
          }}
      >

        {/*title*/}

        <Grid>
          <Typography
              variant="h1"
              className="pt-2"
              test-id="page-title"
          >
            {title}
          </Typography>
        </Grid>

        {/*body*/}

        {
          displayThankYouView ? (
              <ThankYouView
                  clientDetails={clientDetails}
                  thankYouText={thankYouText}
                  enableSurvey={enableSurvey}
              />
          ) : (
              <>
                <Typography sx={{mt: 2, mb: TEXT_BLOCK_MARGIN_BOTTOM}}>
                  {intro}
                </Typography>

                <Grid
                    container
                    sx={{
                      margin: `0 0 ${TEXT_BLOCK_MARGIN_BOTTOM}`,
                      '& .MuiTypography-root': {color: 'primary.main'},
                      '& > *': {marginTop: '0.3rem'},
                    }}
                >
                  {details}
                </Grid>

                <Grid item xs={12}
                      sx={{marginBottom: TEXT_BLOCK_MARGIN_BOTTOM}}>
                  <Typography
                      sx={{fontStyle: 'italic'}}
                      test-id="payment-explanation"
                  >
                    {explanation}
                  </Typography>
                </Grid>

                <Grid item xs={12} sx={{marginBottom: '1rem'}}>
                  <Typography
                      test-id="page-auth-doc"
                  >
                    Please click {' '}
                    <Typography
                        test-id="page-auth-doc-link"
                        component="span"
                        variant="body1"
                        color="error"
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={() => {

                          setModalOpen(true);

                          if (!approvalReq.isAuthDocViewed) {
                            setApprovalReq({
                              ...approvalReq,
                              isAuthDocViewed: true,
                            });
                          }
                        }
                        }
                    >
                      HERE
                    </Typography>
                    {' '} {instructions}
                  </Typography>
                  {programAgreementDetails && (
                      <Typography
                          sx={{ml: 2}}
                          test-id="agreement-details"
                      >
                        {programAgreementDetails}
                      </Typography>
                  )}
                </Grid>
                <Grid sx={{mb: TEXT_BLOCK_MARGIN_BOTTOM}}>
                  <FormControlLabel
                      control={
                        <Checkbox
                            onChange={(e) => {
                              setApprovalReq({
                                ...approvalReq,
                                isCheckboxChecked: e.target.checked,
                              });
                            }}
                            checked={approvalReq.isCheckboxChecked}
                            sx={{
                              color: 'primary.main',
                              '&.Mui-checked': {color: 'primary.main'},
                            }}
                        />}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          color: 'primary.main',
                          fontWeight: 'bold',
                        },
                      }}
                      label={checkboxLabel}
                      test-id="page-approve-checkbox"
                  />
                </Grid>

                {/*approval button*/}

                <Grid container justifyContent="center"
                      sx={{mb: TEXT_BLOCK_MARGIN_BOTTOM}}>
                  <Button
                      test-id="page-approve-btn"
                      spinnerLeftPosition={6}
                      loading={isFormProcessing}
                      disabled={isFormProcessing}
                      onClick={async () => {

                        if (!approvalReq.isAuthDocViewed) {
                          setMessage(GetError(
                              'Please click on the Payment Authorization link and review the terms before clicking approve.'));
                          return;
                        }
                        if (!approvalReq.isCheckboxChecked) {
                          setMessage(GetError(
                              'Please click the checkbox to accept the terms before clicking approve.'));
                          return;
                        }
                        try {
                          setIsFormProcessing(true);
                          await handleApprovedBtnOnClick();
                        } finally {
                          setIsFormProcessing(false);
                        }
                      }}
                  >
                    Approve
                  </Button>
                </Grid>

                {/*footer text*/}

                {
                  footerNotes.map((note: string) => {
                    return (
                        <Typography
                            sx={{
                              mb: TEXT_BLOCK_MARGIN_BOTTOM,
                              ...note.trimStart().startsWith('*') ? {fontStyle: 'italic' } : {}
                        }}
                            test-id="page-approve-footer"
                        >
                          {note}
                        </Typography>
                    );
                  })
                }
                {
                    isFormProcessing && (
                        <div style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          zIndex: 9999,
                        }}>
                          <MobilePaymentsLoader showLoader={true}
                                                customMessage={'Your approval is being processed'}/>
                        </div>
                    )
                }
              </>
          )
        }

        {/*document modal*/}

        {docModal}
      </Grid>
  );

};